import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { PHONE_COLUMN, SENDER_COLUMN } from '../constants/whatsapp';
import type {
  CsvCheck,
  CsvProblem,
  CsvRow,
  ParsedCsv,
  RowToSkip,
} from '../types/whatsapp';

const LOCAL_NUMBER_PATTERN = /^0\d{9}$/;
const NUMBER_MISSING_ZERO_PATTERN = /^[678]\d{8}$/;
const BAD_WHITESPACE_PATTERN = /[\n\r\t]| {4}/;
const COMMA_ERROR_CODES = ['TooFewFields', 'TooManyFields'];

const PHONE_MESSAGE = {
  missing: 'No phone number.',
  local:
    'The number starts with 0. Write it with the country code, for example 27821234567.',
  lostZero:
    'Excel removed the leading 0. Write the number with the country code, for example 27821234567.',
  invalid:
    'Not a phone number we can send to. Write it with the country code, for example 27821234567 or 447911123456.',
};

type PhoneProblem = keyof typeof PHONE_MESSAGE;

export function recipientColumns(variableNumbers: number[]): string[] {
  return [PHONE_COLUMN, SENDER_COLUMN, ...variableNumbers.map(String)];
}

export function expectedHeaderLine(variableNumbers: number[]): string {
  return recipientColumns(variableNumbers).join(',');
}

export function normalizePhone(raw: string): string {
  const kept = String(raw ?? '').replace(/[^\d+]/g, '');

  return kept.startsWith('+') ? kept : `+${kept}`;
}

function findMissingCountryCode(value: string): PhoneProblem | null {
  if (value.startsWith('+')) return null;

  const digits = value.replace(/\D/g, '');
  if (LOCAL_NUMBER_PATTERN.test(digits)) return 'local';
  // Excel drops the leading 0, and some of what is left passes as a foreign
  // number: 0841234567 becomes 841234567, a possible Vietnamese number.
  if (NUMBER_MISSING_ZERO_PATTERN.test(digits)) return 'lostZero';

  return null;
}

function phoneProblem(raw: string): PhoneProblem | null {
  const value = String(raw ?? '').trim();
  if (!value) return 'missing';

  const missingCountryCode = findMissingCountryCode(value);
  if (missingCountryCode) return missingCountryCode;
  if (isPossiblePhoneNumber(normalizePhone(value))) return null;

  return 'invalid';
}

function problem(message: string, lineNumbers: number[]): CsvProblem[] {
  return lineNumbers.length > 0 ? [{ message, lineNumbers }] : [];
}

function checkHeader(
  csv: ParsedCsv,
  variableNumbers: number[],
): CsvProblem | null {
  const wanted = expectedHeaderLine(variableNumbers);
  if (csv.headerLine === wanted) return null;

  return {
    message: `The CSV header must be "${wanted}" but it is "${csv.headerLine}".`,
    lineNumbers: [1],
  };
}

function linesWithParseErrors(
  csv: ParsedCsv,
  isWanted: (code: string) => boolean,
): number[] {
  return csv.parseErrors
    .filter((error) => isWanted(error.code))
    .map((error) => error.lineNumber);
}

function checkCommaErrors(csv: ParsedCsv): CsvProblem[] {
  return problem(
    "The columns don't match the header — an extra or missing comma.",
    linesWithParseErrors(csv, (code) => COMMA_ERROR_CODES.includes(code)),
  );
}

// An open quote swallows every line after it into one cell, so those people
// never become rows and could not be listed as skipped. Block the whole sheet.
function checkQuoteErrors(csv: ParsedCsv): CsvProblem[] {
  return problem(
    "A quote mark is never closed, so we can't tell where the rows start and end. Fix it in your sheet and add it again.",
    linesWithParseErrors(csv, (code) => !COMMA_ERROR_CODES.includes(code)),
  );
}

function checkNotEmpty(csv: ParsedCsv): CsvProblem[] {
  if (csv.rows.length > 0) return [];

  return [
    { message: 'The sheet has no rows under the header.', lineNumbers: [] },
  ];
}

function checkPhones(csv: ParsedCsv): CsvProblem[] {
  const linesByProblem = new Map<PhoneProblem, number[]>();

  csv.rows.forEach(({ lineNumber, cells }) => {
    const found = phoneProblem(cells[PHONE_COLUMN]);
    if (!found) return;

    linesByProblem.set(found, [
      ...(linesByProblem.get(found) ?? []),
      lineNumber,
    ]);
  });

  return [...linesByProblem].flatMap(([found, lineNumbers]) =>
    problem(PHONE_MESSAGE[found], lineNumbers),
  );
}

function checkDuplicates(
  csv: ParsedCsv,
  linesAlreadySkipped: Set<number>,
): CsvProblem[] {
  const seen = new Set<string>();
  const repeatedLines: number[] = [];

  csv.rows.forEach(({ lineNumber, cells }) => {
    if (linesAlreadySkipped.has(lineNumber)) return;

    const phone = normalizePhone(cells[PHONE_COLUMN]);
    if (seen.has(phone)) {
      repeatedLines.push(lineNumber);
      return;
    }
    seen.add(phone);
  });

  return problem(
    'The same number is on an earlier row. Only the first one is sent.',
    repeatedLines,
  );
}

function checkSenderColumn(csv: ParsedCsv): CsvProblem[] {
  const filledLines = csv.rows
    .filter(({ cells }) => (cells[SENDER_COLUMN] ?? '').trim() !== '')
    .map(({ lineNumber }) => lineNumber);

  return problem(
    "Leave the sender column blank. All messages go from ASSA's WhatsApp number.",
    filledLines,
  );
}

function checkOneVariableColumn(
  rows: CsvRow[],
  columnName: string,
): CsvProblem[] {
  const emptyLines: number[] = [];
  const whitespaceLines: number[] = [];

  rows.forEach(({ lineNumber, cells }) => {
    const cell = cells[columnName] ?? '';
    if (cell.trim() === '') {
      emptyLines.push(lineNumber);
      return;
    }
    if (BAD_WHITESPACE_PATTERN.test(cell)) whitespaceLines.push(lineNumber);
  });

  return [
    ...problem(
      `Column ${columnName} is empty — those people would get the message with {{${columnName}}} still in the text.`,
      emptyLines,
    ),
    ...problem(
      `Column ${columnName} has a line break, tab, or long run of spaces. WhatsApp rejects those messages.`,
      whitespaceLines,
    ),
  ];
}

function checkVariableColumns(
  csv: ParsedCsv,
  variableNumbers: number[],
): CsvProblem[] {
  return variableNumbers.flatMap((number) =>
    checkOneVariableColumn(csv.rows, String(number)),
  );
}

function linesWithProblems(problems: CsvProblem[]): Set<number> {
  return new Set(problems.flatMap(({ lineNumbers }) => lineNumbers));
}

function checkRows(csv: ParsedCsv, variableNumbers: number[]): CsvProblem[] {
  const otherProblems = [
    ...checkCommaErrors(csv),
    ...checkPhones(csv),
    ...checkSenderColumn(csv),
    ...checkVariableColumns(csv, variableNumbers),
  ];

  // Duplicates come last, so a row that is skipped anyway never claims the
  // number and pushes out a later row that could have been sent.
  return [
    ...otherProblems,
    ...checkDuplicates(csv, linesWithProblems(otherProblems)),
  ];
}

function groupReasonsByLine(rowProblems: CsvProblem[]): Map<number, string[]> {
  const reasonsByLine = new Map<number, string[]>();

  rowProblems.forEach(({ message, lineNumbers }) => {
    lineNumbers.forEach((lineNumber) => {
      reasonsByLine.set(lineNumber, [
        ...(reasonsByLine.get(lineNumber) ?? []),
        message,
      ]);
    });
  });

  return reasonsByLine;
}

function buildRowToSkip(row: CsvRow, reasons: string[]): RowToSkip {
  return {
    lineNumber: row.lineNumber,
    phone: row.cells[PHONE_COLUMN] ?? '',
    reasons,
  };
}

function splitRowsToSendAndSkip(csv: ParsedCsv, rowProblems: CsvProblem[]) {
  const reasonsByLine = groupReasonsByLine(rowProblems);
  const hasProblem = (row: CsvRow) => reasonsByLine.has(row.lineNumber);

  return {
    rowsToSkip: csv.rows
      .filter(hasProblem)
      .map((row) =>
        buildRowToSkip(row, reasonsByLine.get(row.lineNumber) ?? []),
      ),
    rowsToSend: csv.rows.filter((row) => !hasProblem(row)),
  };
}

export function validateBulkRows(
  csv: ParsedCsv,
  variableNumbers: number[],
): CsvCheck {
  const headerProblem = checkHeader(csv, variableNumbers);
  if (headerProblem) {
    return {
      sheetProblems: [headerProblem],
      rowsToSkip: [],
      rowsToSend: [],
    };
  }

  const rowProblems = checkRows(csv, variableNumbers);

  return {
    sheetProblems: [...checkNotEmpty(csv), ...checkQuoteErrors(csv)],
    ...splitRowsToSendAndSkip(csv, rowProblems),
  };
}
